/**
 * HomePlannerPreview.jsx — Jun 2026.
 *
 * Read-only Day / Week / Month preview of the teacher's Planner, shown
 * as the first section of the signed-in Home view (above the Today
 * card). Pro-only, mirroring the Planner's own three views.
 *
 *   · Day / Week / Month view switcher + prev/next date navigation
 *     scroll through the schedule LOCALLY (no page change).
 *   · Every schedule "section" (day header, period cell, lesson chip)
 *     is CLICKABLE but NOT editable — any content click opens the full
 *     Planner at that date. No scheduling / notes / drag here.
 *   · Reads the same planner data (schedule + day-labels + events) so
 *     it reflects Planner changes on load and on window refocus.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import {
  getPlannerSchedule,
  listPlannerEvents,
  plannerDayLabels,
} from "../../lib/api";
import { subjectColor, tint } from "../../lib/subjectPalette";

// ---- date helpers (mirror Planner.jsx) ----
const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const startOfWeekMonday = (d) => {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // 0 = Monday
  return addDays(x, -dow);
};
const fmtLong = (d) =>
  d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
const fmtShort = (d) =>
  d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
const fmtMonth = (d) =>
  d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

const _BLOCK_KINDS = new Set(["holiday", "pa_day", "field_trip", "custom"]);

const _isProUser = (user) => {
  if (!user) return false;
  if (user.has_math_engine_access) return true;
  if (!user.subscription?.is_active) return false;
  return ["pro", "pro_plus"].includes(
    (user.subscription.tier_id || "").toLowerCase(),
  );
};

const VIEWS = [
  { key: "day", label: "Day", icon: CalendarDays },
  { key: "week", label: "Week", icon: CalendarRange },
  { key: "month", label: "Month", icon: CalendarIcon },
];

export default function HomePlannerPreview({ user }) {
  const isPro = _isProUser(user);
  const navigate = useNavigate();
  const [view, setView] = useState("day");
  const [cursor, setCursor] = useState(() => new Date());
  const [schedule, setSchedule] = useState(null); // null = loading
  const [labels, setLabels] = useState({}); // iso -> day label | null
  const [events, setEvents] = useState([]);

  // The set of dates the current view spans.
  const dates = useMemo(() => {
    if (view === "day") return [new Date(cursor)];
    if (view === "week") {
      const mon = startOfWeekMonday(cursor);
      return Array.from({ length: 5 }, (_, i) => addDays(mon, i));
    }
    // month — 6x7 grid starting from the Sunday on/before the 1st.
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = first.getDay(); // 0 = Sunday
    const gridStart = addDays(first, -offset);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [view, cursor]);

  useEffect(() => {
    if (!isPro) return;
    let alive = true;
    getPlannerSchedule()
      .then((s) => alive && setSchedule(s || {}))
      .catch(() => alive && setSchedule({}));
    return () => {
      alive = false;
    };
  }, [isPro]);

  const openPlanner = useCallback(
    (d) => {
      const iso = isoDate(d || cursor);
      navigate(`/dashboard?view=planner&date=${iso}`);
    },
    [navigate, cursor],
  );

  const refetch = useCallback(async () => {
    if (!isPro || dates.length === 0) return;
    const isos = dates.map(isoDate);
    const start = isos[0];
    const end = isos[isos.length - 1];
    const [ev, dl] = await Promise.all([
      listPlannerEvents(start, end).catch(() => []),
      plannerDayLabels(isos)
        .then((r) => r?.labels || {})
        .catch(() => ({})),
    ]);
    setEvents(Array.isArray(ev) ? ev : []);
    setLabels(dl || {});
  }, [isPro, dates]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Refocus refresh — reflect any Planner edits made in another tab.
  useEffect(() => {
    if (!isPro) return undefined;
    const onFocus = () => refetch();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isPro, refetch]);

  if (!isPro) return null;
  if (schedule === null) {
    return (
      <section className="ae-card p-6" data-testid="home-planner-preview-loading">
        <div className="h-4 w-40 rounded bg-[#4a6984]/10 animate-pulse" />
        <div className="mt-4 h-24 rounded bg-[#4a6984]/10 animate-pulse" />
      </section>
    );
  }

  const teachingPeriods = (schedule.periods || []).filter(
    (p) => p.kind !== "break",
  );
  const hasSchedule = teachingPeriods.length > 0;

  // No planner set up yet — soft CTA into the full Planner.
  if (!hasSchedule) {
    return (
      <section className="ae-card p-6 md:p-7" data-testid="home-planner-preview-empty">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-9 w-9 rounded-lg bg-[#f4f5f7] border border-[#4a6984]/15 grid place-items-center">
            <CalendarDays className="h-4 w-4 text-[#4a6984]" />
          </div>
          <span className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
            Planner
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/dashboard?view=planner")}
          data-testid="home-planner-preview-setup"
          className="inline-flex items-center gap-1 font-semibold text-[#1c2d3d] hover:text-[#4a6984] transition-colors"
        >
          Set up your Planner to see your week at a glance
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </section>
    );
  }

  const smap = schedule.subject_map || {};
  const eventsFor = (iso, periodLabel) =>
    events.find(
      (e) =>
        e.date === iso &&
        e.period_label === periodLabel &&
        (e.kind === "lesson" || e.kind === "work"),
    );
  const blockFor = (iso) =>
    events.find(
      (e) => e.date === iso && !e.period_label && _BLOCK_KINDS.has(e.kind),
    );
  const lessonCountFor = (iso) =>
    events.filter(
      (e) => e.date === iso && (e.kind === "lesson" || e.kind === "work"),
    ).length;

  const goPrev = () =>
    setCursor((c) =>
      view === "day"
        ? addDays(c, -1)
        : view === "week"
        ? addDays(c, -7)
        : new Date(c.getFullYear(), c.getMonth() - 1, 1),
    );
  const goNext = () =>
    setCursor((c) =>
      view === "day"
        ? addDays(c, 1)
        : view === "week"
        ? addDays(c, 7)
        : new Date(c.getFullYear(), c.getMonth() + 1, 1),
    );
  const goToday = () => setCursor(new Date());

  const rangeLabel =
    view === "day"
      ? fmtLong(cursor)
      : view === "week"
      ? `${fmtShort(dates[0])} – ${fmtShort(dates[dates.length - 1])}`
      : fmtMonth(cursor);

  // ---- one clickable subject/lesson cell ----
  const Cell = ({ dateObj, iso, subject, ev, compact }) => {
    const color = subjectColor(subject);
    if (ev) {
      return (
        <button
          type="button"
          onClick={() => openPlanner(dateObj)}
          data-testid={`home-planner-cell-${iso}-${ev.period_label}`}
          className="w-full text-left rounded p-2 hover:brightness-95 transition"
          style={{ backgroundColor: tint(color, 0.25), borderLeft: `3px solid ${color}` }}
          title="Open in Planner"
        >
          <div className={`font-semibold text-[#1c2d3d] leading-tight line-clamp-2 ${compact ? "text-[10px]" : "text-xs"}`}>
            {ev.activity_name || ev.work_prompt || "Work period"}
          </div>
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => openPlanner(dateObj)}
        data-testid={`home-planner-cell-${iso}`}
        className={`w-full text-left rounded px-2 py-1.5 text-[#4a6984]/70 hover:bg-[#96ded1]/10 transition ${compact ? "text-[10px]" : "text-xs"}`}
        style={{ borderLeft: `3px solid ${tint(color, 0.4)}` }}
        title="Open in Planner"
      >
        {subject && subject !== "Prep" ? subject : "—"}
      </button>
    );
  };

  return (
    <section className="ae-card overflow-hidden" data-testid="home-planner-preview">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-[#4a6984]/10 bg-[#f4f5f7]/50">
        <div className="flex items-center gap-2">
          <span className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
            Planner
          </span>
          <span className="text-xs font-semibold text-[#1c2d3d]" data-testid="home-planner-range-label">
            {rangeLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* View switcher */}
          <div className="inline-flex rounded-lg bg-white border border-[#4a6984]/15 p-0.5">
            {VIEWS.map((v) => {
              const active = view === v.key;
              return (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => setView(v.key)}
                  data-testid={`home-planner-view-${v.key}`}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                    active
                      ? "bg-[#1c2d3d] text-white"
                      : "text-[#4a6984] hover:bg-[#f4f5f7]"
                  }`}
                >
                  <v.icon className="h-3 w-3" />
                  <span className="hidden sm:inline">{v.label}</span>
                </button>
              );
            })}
          </div>
          {/* Date navigation */}
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={goPrev}
              data-testid="home-planner-prev"
              aria-label="Previous"
              className="h-7 w-7 grid place-items-center rounded-md border border-[#4a6984]/15 bg-white text-[#4a6984] hover:bg-[#f4f5f7]"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={goToday}
              data-testid="home-planner-today"
              className="px-2 py-1 rounded-md border border-[#4a6984]/15 bg-white text-[11px] font-bold uppercase tracking-wide text-[#4a6984] hover:bg-[#f4f5f7]"
            >
              Today
            </button>
            <button
              type="button"
              onClick={goNext}
              data-testid="home-planner-next"
              aria-label="Next"
              className="h-7 w-7 grid place-items-center rounded-md border border-[#4a6984]/15 bg-white text-[#4a6984] hover:bg-[#f4f5f7]"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ---- DAY ---- */}
      {view === "day" && (() => {
        const iso = isoDate(cursor);
        const dayLabel = labels[iso];
        const block = blockFor(iso);
        if (block) {
          return (
            <div className="p-6 text-center" data-testid="home-planner-day">
              <p className="text-sm font-bold uppercase tracking-wider text-[#1c2d3d]">
                {block.kind === "holiday" ? "Holiday" : block.kind === "pa_day" ? "PA Day" : block.kind === "field_trip" ? "Field Trip" : "Day off"}
              </p>
              {block.activity_name && <p className="text-[#4a6984] mt-1 text-sm">{block.activity_name}</p>}
            </div>
          );
        }
        if (!dayLabel) {
          return (
            <div className="p-6 text-center text-[#4a6984]" data-testid="home-planner-day">
              <p className="text-sm font-bold uppercase tracking-wider">No school day</p>
            </div>
          );
        }
        const dayPeriods = smap[dayLabel] || {};
        return (
          <div className="divide-y divide-[#4a6984]/10" data-testid="home-planner-day">
            {teachingPeriods.map((p) => {
              const subject = dayPeriods[p.label] || "";
              const ev = eventsFor(iso, p.label);
              return (
                <div key={p.label} className="grid grid-cols-[5.5rem_1fr] items-center gap-2 px-4 py-2">
                  <div className="text-xs">
                    <div className="font-bold text-[#1c2d3d]">{p.label}</div>
                    <div className="text-[#4a6984]/70">{p.start}–{p.end}</div>
                  </div>
                  <Cell dateObj={cursor} iso={iso} subject={subject} ev={ev} />
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ---- WEEK ---- */}
      {view === "week" && (
        <div className="overflow-x-auto" data-testid="home-planner-week">
          <table className="w-full text-xs">
            <thead className="bg-[#f4f5f7]/60">
              <tr>
                <th className="text-left p-2 text-[#4a6984] font-semibold w-20">Period</th>
                {dates.map((d) => {
                  const iso = isoDate(d);
                  const isToday = iso === isoDate(new Date());
                  return (
                    <th
                      key={iso}
                      data-testid={isToday ? "home-planner-week-today-col" : undefined}
                      className={`text-left p-2 border-l border-[#4a6984]/10 ${
                        isToday ? "bg-[#96ded1]/25" : ""
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => openPlanner(d)}
                        data-testid={`home-planner-week-day-${iso}`}
                        className="text-left hover:text-[#1c2d3d] transition-colors"
                        title="Open in Planner"
                      >
                        <div className="font-bold text-[#1c2d3d] flex items-center gap-1.5">
                          {fmtShort(d)}
                          {isToday && (
                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-[#1c2d3d] text-white text-[8px] font-extrabold uppercase tracking-[0.12em]">
                              Today
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#4a6984]/70">{labels[iso] || "—"}</div>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {teachingPeriods.map((p) => (
                <tr key={p.label} className="border-t border-[#4a6984]/10">
                  <td className="p-2 align-top whitespace-nowrap">
                    <div className="font-bold text-[#1c2d3d]">{p.label}</div>
                    <div className="text-[10px] text-[#4a6984]/70">{p.start}–{p.end}</div>
                  </td>
                  {dates.map((d) => {
                    const iso = isoDate(d);
                    const isToday = iso === isoDate(new Date());
                    const dayLabel = labels[iso];
                    const block = blockFor(iso);
                    if (!dayLabel || block) {
                      return (
                        <td key={iso} className={`p-1 align-top border-l border-[#4a6984]/10 ${isToday ? "bg-[#96ded1]/15" : "bg-[#4a6984]/5"}`}>
                          <div className="text-[10px] text-[#4a6984]/40 italic px-1">
                            {block ? (block.kind === "pa_day" ? "PA Day" : block.kind === "holiday" ? "Holiday" : "—") : "—"}
                          </div>
                        </td>
                      );
                    }
                    const subject = smap[dayLabel]?.[p.label] || "";
                    const ev = eventsFor(iso, p.label);
                    return (
                      <td key={iso} className={`p-1 align-top min-w-[7rem] border-l border-[#4a6984]/10 ${isToday ? "bg-[#96ded1]/15" : ""}`}>
                        <Cell dateObj={d} iso={iso} subject={subject} ev={ev} compact />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- MONTH ---- */}
      {view === "month" && (
        <div data-testid="home-planner-month">
          <div className="grid grid-cols-7 text-[10px]">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((l) => (
              <div key={l} className="px-2 py-1.5 bg-[#f4f5f7]/60 text-[#4a6984] font-semibold text-center border-b border-[#4a6984]/10">
                {l}
              </div>
            ))}
            {dates.map((d) => {
              const iso = isoDate(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const block = blockFor(iso);
              const count = lessonCountFor(iso);
              const isToday = iso === isoDate(new Date());
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => openPlanner(d)}
                  data-testid={`home-planner-month-day-${iso}`}
                  className={`min-h-[3.5rem] border-b border-r border-[#4a6984]/10 p-1.5 text-left align-top transition-colors hover:bg-[#96ded1]/10 ${
                    inMonth ? "bg-white" : "bg-[#f4f5f7]/40 text-[#4a6984]/40"
                  }`}
                  title="Open in Planner"
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-[11px] font-bold ${isToday ? "inline-grid place-items-center h-5 w-5 rounded-full bg-[#1c2d3d] text-white" : inMonth ? "text-[#1c2d3d]" : ""}`}>
                      {d.getDate()}
                    </span>
                    {block && (
                      <span className="text-[8px] font-bold uppercase tracking-wide text-[#c45a6b]">
                        {block.kind === "pa_day" ? "PA" : block.kind === "holiday" ? "Hol" : "•"}
                      </span>
                    )}
                  </div>
                  {count > 0 && !block && (
                    <span
                      className="mt-1 inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full bg-[#96ded1]/45 text-[#1c2d3d] text-[9px] font-extrabold"
                      data-testid={`home-planner-month-count-${iso}`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer — open full planner */}
      <div className="px-5 py-2.5 border-t border-[#4a6984]/10 bg-[#f4f5f7]/40 text-right">
        <button
          type="button"
          onClick={() => openPlanner(cursor)}
          data-testid="home-planner-open-full"
          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#4a6984] hover:text-[#1c2d3d] transition-colors"
        >
          Open full Planner <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </section>
  );
}
